import { useState, useCallback } from 'react';
import { Text } from 'react-native';
import { Flex, IconClose, IconTooltip } from '@banhala/ui-kit';
import type { NotificationGuideProps } from './types';
import { bridge, isNil } from '../../../../utils';
import { HTMLText } from '../../../HTMLText';
import { useOpenDeeplink } from '../../../../hooks';
import { SimpleGuidePopup } from './SimpleGuidePopup';

export const NotificationGuide = ({ entity }: NotificationGuideProps) => {
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(new Set());
  const [popupImage, setPopupImage] = useState<string | null>(null);
  const { openDeeplink } = useOpenDeeplink();

  const handleDismiss = useCallback((key: string) => {
    setDismissedKeys(prev => new Set([...prev, key]));
  }, []);

  const handlePress = useCallback((data: NonNullable<typeof entity>[0]) => {
    const { item, logging } = data;
    if (item.action_type === 'DEEPLINK' && item.contents.deeplink) {
      openDeeplink(item.contents.deeplink);
    } else if (item.action_type === 'POPUP' && item.contents.popup_image) {
      setPopupImage(item.contents.popup_image);
      if (logging) {
        bridge.logAnalyticsEvent({
          name: 'VIEW_POPUP',
          analytics: logging.analytics,
          ably_only_log: logging.ably_only_log,
        });
      }
    }
  }, [openDeeplink]);

  const handleClosePopup = useCallback(() => {
    setPopupImage(null);
  }, []);

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <>
      <Flex.Column padding={{ horizontal: 12, vertical: 8 }} gap={8}>
        {entity.map((data) => {
          const { item } = data;
          if (dismissedKeys.has(item.key)) {
            return null;
          }

          const { contents } = item;
          const backgroundColor = contents.background_color || '#FF5160'; // bg_pink_primary

          return (
            <Flex.Row
              key={item.key}
              backgroundColorValue={backgroundColor}
              borderRadius={8}
              alignItems="flex-start"
              padding={{ vertical: 12, horizontal: 8 }}
              gap={4}
              onPress={() => handlePress(data)}
              disabled={item.action_type === 'NONE'}
            >
              {/* 닫기 버튼 - 왼쪽 */}
              {item.deletable && (
                <Flex
                  padding={{ top: 6 }}
                  onPress={() => handleDismiss(item.key)}
                >
                  <IconClose width={12} height={12} color="content_tertiary" />
                </Flex>
              )}

              {/* 뱃지 - subTitle5: fontSize 11, fontWeight 600 */}
              {contents.badge && (
                <Flex
                  backgroundColorValue={contents.badge.background_color || '#FFDFE4'}
                  borderRadius={50}
                  padding={{ horizontal: 6, vertical: 4 }}
                >
                  <Text
                    style={{
                      color: contents.badge.text_color || '#E21F2C',
                      fontSize: 11,
                      fontWeight: '600',
                    }}
                  >
                    {contents.badge.text}
                  </Text>
                </Flex>
              )}

              {/* 설명 텍스트 */}
              <Flex flex={1} padding={{ top: 2 }}>
                <HTMLText
                  type="body3"
                  color="content_primary"
                  numberOfLines={2}
                  ellipsizeMode="tail"
                >
                  {contents.description}
                </HTMLText>
              </Flex>

              {/* End 아이콘 - 오른쪽 */}
              {contents.show_end_icon && (
                <Flex padding={{ top: 4 }}>
                  <IconTooltip width={16} height={16} color="content_primary" />
                </Flex>
              )}
            </Flex.Row>
          );
        })}
      </Flex.Column>

      {/* Popup Modal */}
      {popupImage && (
        <SimpleGuidePopup
          visible={!!popupImage}
          imageUrl={popupImage}
          onClose={handleClosePopup}
        />
      )}
    </>
  );
};
